import { useState, useRef, useEffect, useLayoutEffect, type ElementType } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { PlusCircle, History, Square, ArrowUp, PanelRightClose } from 'lucide-react';
import { toast } from 'sonner';
import { useChat } from '../../hooks/use-chat';
import { usePageEntity } from '../../contexts/PageContext';
import { ChatHistoryPanel } from './ChatHistoryPanel';
import { ChatMetricsBar } from './ChatMetricsBar';
import { ChatMessages } from './ChatMessages';
import { ConfirmDialog } from '../ui/confirm-dialog';
import type { ChatCard } from '../../types/chat';

const MAX_COMPOSER_HEIGHT = 144; // ~6 rows

// Starter prompt suggestions (entity-aware ones appended in component)
const BASE_SUGGESTIONS = [
  'Add a contact',
  "What's on my calendar this week?",
  'Show my recent opportunities',
];

export function AiSidebar({ open, onOpenChange, initialPrompt, onPromptConsumed, docked = false }: { open: boolean; onOpenChange: (v: boolean) => void; initialPrompt?: string | null; onPromptConsumed?: () => void; docked?: boolean }) {
  const { messages, streamingText, isSending, error, send, confirm, threadId, newChat, loadThread, deleteThread, rename, stop, retry, regenerate, editAndResend, undo, canUndo, runRowAction, canvasSignal, turnMeta } = useChat();
  const entity = usePageEntity();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ChatCard>>({});
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});
  const [undoingKey, setUndoingKey] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  // Pending undo awaiting confirmation (replaces window.confirm).
  const [pendingUndo, setPendingUndo] = useState<{ key: string; card: ChatCard } | null>(null);

  // Auto-grow textarea ref
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Auto-scroll refs — bottomRef is the sentinel; scrollContainerRef is the overflow-y-auto div
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // The main pane follows the conversation. Navigate on the first signal for a
  // draft; once the canvas is already showing it, only refresh the data — a repeat
  // navigate() on every revision would fight the user's cursor mid-edit.
  useEffect(() => {
    if (!canvasSignal) return;
    const target = `/draft/${canvasSignal.draftId}`;
    void queryClient.invalidateQueries({ queryKey: ['drafts', canvasSignal.draftId] });
    if (location.pathname !== target) navigate(target);
  }, [canvasSignal?.draftId, canvasSignal?.revision]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    const nearBottom = el ? (el.scrollHeight - el.scrollTop - el.clientHeight) < 120 : true;
    if (nearBottom) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingText, results, dismissed]);

  // Auto-grow the composer when draft changes — useLayoutEffect avoids post-paint flash
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // Guard: only set height when scrollHeight is measurable (avoids 0 in jsdom/paint flush)
    if (el.scrollHeight > 0) {
      el.style.height = Math.min(el.scrollHeight, MAX_COMPOSER_HEIGHT) + 'px';
    }
  }, [draft]);

  const doSend = async (text?: string) => {
    const content = (text ?? draft).trim();
    if (!content) return;
    setDraft('');
    await send(
      content,
      entity ? { type: entity.type, id: entity.id, name: entity.name, data: entity.data } : undefined,
    );
  };

  // Send a suggestion chip text directly — does NOT read or clear the composer draft.
  const sendSuggestion = (text: string) => {
    const content = text.trim();
    if (!content) return;
    void send(
      content,
      entity ? { type: entity.type, id: entity.id, name: entity.name, data: entity.data } : undefined,
    );
  };

  // A prompt handed in from outside (the Today ask bar) — open prefilled and submit it
  // once. The ref guards against re-firing on re-render while the same prompt is set.
  const consumedPromptRef = useRef<string | null>(null);
  useEffect(() => {
    if (open && initialPrompt && consumedPromptRef.current !== initialPrompt) {
      consumedPromptRef.current = initialPrompt;
      sendSuggestion(initialPrompt);
      onPromptConsumed?.();
    }
    if (!open) consumedPromptRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPrompt]);

  const onConfirm = async (key: string, card: ChatCard) => {
    setConfirmingKey(key);
    try {
      const { link } = await confirm(card);
      setResults((r) => ({ ...r, [key]: { ...card, kind: 'result', link } }));
    } catch (e) {
      // try/finally with no catch meant a rejected write cleared the spinner and
      // changed nothing else: no toast, no error, no result card. Tapping Confirm
      // on a card the server refused was indistinguishable from tapping nothing,
      // which is how a doomed email draft read as a dead button. The card is left
      // in place deliberately so the action can be retried once the cause is fixed.
      const reason = e instanceof Error ? e.message : 'The server rejected it.';
      toast.error(reason.slice(0, 200), { description: 'Nothing was saved. The card is still here to retry.' });
    } finally {
      setConfirmingKey(null);
    }
  };

  // Undo now asks for confirmation via an on-brand card, not window.confirm.
  const requestUndo = (key: string, card: ChatCard) => {
    setPendingUndo({ key, card });
  };

  const runUndo = async () => {
    if (!pendingUndo) return;
    const { key, card } = pendingUndo;
    setUndoingKey(key);
    try {
      const { ok: undoOk } = await undo(card);
      if (undoOk) {
        // An update revert leaves the card in place (it no longer reflects state);
        // a create undo removes the created record, so dismiss its card.
        if (card.action === 'create') setDismissed((d) => ({ ...d, [key]: true }));
        toast.success(card.action === 'update' ? 'Change reverted' : 'Undone');
      } else {
        toast.error('Could not undo');
      }
    } catch {
      toast.error('Could not undo');
    } finally {
      setUndoingKey(null);
      setPendingUndo(null);
    }
  };

  const handleSelectThread = async (id: string) => {
    await loadThread(id);
    setShowHistory(false);
    setResults({});
    setDismissed({});
  };

  const handleDeleteThread = async (id: string) => {
    // Capture threadId before the async delete so stale closure doesn't compare against a changed value.
    const currentThreadId = threadId;
    await deleteThread(id);
    if (id === currentThreadId) {
      newChat();
      setResults({});
      setDismissed({});
    }
  };

  const handleNewChat = () => {
    newChat();
    setResults({});
    setDismissed({});
    setShowHistory(false);
  };

  // Starter chip suggestions
  const suggestions = [
    ...BASE_SUGGESTIONS,
    ...(entity ? [`Add a note about ${entity.name}`] : []),
  ];

  // Build per-card undo handlers for ChatMessages
  const buildUndoHandler = (key: string, card: ChatCard) => {
    const shown = results[key] ?? card;
    if (shown.kind !== 'result' || !canUndo(shown)) return undefined;
    return () => requestUndo(key, shown);
  };

  // In docked mode we're not inside a Radix Dialog, so SheetTitle (Dialog.Title)
  // would throw — fall back to a plain heading. In Sheet mode SheetTitle keeps the
  // dialog accessible.
  const TitleTag: ElementType = docked ? 'h2' : SheetTitle;

  const body = (
    <>
      <div className="border-b p-3">
        <div className="flex items-center justify-between">
          <TitleTag className="text-sm font-semibold">
            Friday assistant{entity ? ` · ${entity.name}` : ''}
          </TitleTag>
          {/* In Sheet mode pr-8 reserves space for the Sheet's absolute top-right close (✕) button so it never overlaps these controls */}
          <div className={`flex items-center gap-1 ${docked ? '' : 'pr-8'}`}>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleNewChat}
              title="New chat"
              aria-label="New chat"
            >
              <PlusCircle className="h-4 w-4" />
            </Button>
            <Button
              variant={showHistory ? 'secondary' : 'ghost'}
              size="icon"
              className="h-7 w-7"
              onClick={() => setShowHistory((v) => !v)}
              title="Recent conversations"
              aria-label="History"
            >
              <History className="h-4 w-4" />
            </Button>
            {docked && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onOpenChange(false)}
                title="Hide assistant (⌘J)"
                aria-label="Hide assistant"
              >
                <PanelRightClose className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Troubleshooting strip. Sits under the header and above history so the
          thread id keeps its place whichever panel is open. */}
      <ChatMetricsBar meta={turnMeta} threadId={threadId} />

      {showHistory && (
          <ChatHistoryPanel
            activeThreadId={threadId}
            onSelect={handleSelectThread}
            onDelete={handleDeleteThread}
            onRename={rename}
          />
        )}

        <div ref={scrollContainerRef} className="flex-1 space-y-3 overflow-y-auto p-3">
          {messages.length === 0 && !isSending && (
            <>
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Ask me to add a contact, create an opportunity, update a record, or look something up.
              </p>
              {/* Starter prompt chips */}
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {suggestions.map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-full px-3 text-xs"
                    onClick={() => sendSuggestion(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </>
          )}

          <ChatMessages
            messages={messages}
            streamingText={streamingText}
            isSending={isSending}
            error={error}
            onRegenerate={() => void regenerate()}
            onEditAndResend={editAndResend}
            onRetry={() => void retry()}
            confirmingKey={confirmingKey}
            results={results}
            dismissed={dismissed}
            undoingKey={undoingKey}
            onConfirm={(key, card) => void onConfirm(key, card)}
            onDismiss={(key) => setDismissed((d) => ({ ...d, [key]: true }))}
            buildUndoHandler={buildUndoHandler}
            onRowAction={runRowAction}
            bottomRef={bottomRef}
          />
        </div>

        <div className="flex items-end gap-2 border-t p-3">
          <Textarea
            ref={textareaRef}
            placeholder="Message Friday…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void doSend();
              }
              // Shift+Enter: let browser insert newline (no-op here)
            }}
            disabled={isSending}
            rows={1}
            className="min-h-[36px] flex-1 resize-none overflow-hidden py-2 text-sm"
            style={{ maxHeight: `${MAX_COMPOSER_HEIGHT}px` }}
          />
          {isSending ? (
            <Button size="sm" variant="outline" onClick={stop} aria-label="Stop" className="mb-0 shrink-0">
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => void doSend()}
              disabled={!draft.trim()}
              aria-label="Send"
              className="mb-0 shrink-0"
            >
              <ArrowUp className="h-4 w-4" />
              <span className="sr-only">Send</span>
            </Button>
          )}
        </div>
    </>
  );

  const confirmDialog = (
    <ConfirmDialog
      open={pendingUndo !== null}
      onOpenChange={(v) => {
        if (!v && undoingKey === null) setPendingUndo(null);
      }}
      title={pendingUndo?.card.action === 'update' ? 'Revert this change?' : 'Undo this action?'}
      description={
        pendingUndo
          ? pendingUndo.card.action === 'update'
            ? `This puts "${pendingUndo.card.title}" back the way it was.`
            : `This removes "${pendingUndo.card.title}" that Friday just created.`
          : undefined
      }
      confirmLabel={pendingUndo?.card.action === 'update' ? 'Revert' : 'Undo'}
      destructive={pendingUndo?.card.action !== 'update'}
      busy={undoingKey !== null}
      onConfirm={() => void runUndo()}
    />
  );

  // Docked: an in-flow panel that sits alongside page content (wide screens) so both
  // are usable at once. Only mounts when open, so it takes no layout width when hidden.
  if (docked) {
    if (!open) return null;
    return (
      <>
        <aside
          aria-label="Friday assistant"
          className="glass-chrome my-3 mr-3 hidden w-[400px] shrink-0 flex-col overflow-hidden rounded-2xl xl:flex"
        >
          {body}
        </aside>
        {confirmDialog}
      </>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-[420px] max-w-[92vw] flex-col p-0">
        {body}
      </SheetContent>
      {confirmDialog}
    </Sheet>
  );
}
